import React, { useState, useEffect, useId } from 'react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { SimpleTooltip } from '@/components/ui/Tooltip/Tooltip';
import { AttachmentsService } from '@/services/AttachmentsService';
import { AttachmentsManager } from '@/components/Attachments/AttachmentsManager';
import { t } from '@/lib/i18n';
import { ImageIcon, X, RefreshCw } from 'lucide-react';
import type { GalleryImage } from '@/types/attachment';

const attachmentsService = new AttachmentsService();

export interface ImageAttachmentFieldProps {
  /** The attachment id (or null when no image is set). */
  value: string | null;
  onChange: (id: string | null) => void;
  label: string;
  hint?: string;
  disabled?: boolean;
  /**
   * `horizontal` (default): small thumbnail next to title + actions column.
   * Good for wide forms / modals.
   * `vertical`: filename on top, large thumbnail in the middle, actions
   * below. Designed for narrow side-columns (e.g. inline row editor).
   */
  layout?: 'horizontal' | 'vertical';
}

/**
 * An inline field for picking a single image from the attachments library.
 *
 * - Empty state: label + "Add image" button → opens AttachmentsManager in pickMode.
 * - Populated state: thumbnail + "Change" + "Remove" buttons.
 * - Remove fires onChange(null) with a ConfirmDialog.
 */
export function ImageAttachmentField({
  value,
  onChange,
  label,
  hint,
  disabled = false,
  layout = 'horizontal',
}: ImageAttachmentFieldProps) {
  const fieldId = useId();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [attachment, setAttachment] = useState<GalleryImage | null>(null);
  const [loadingAttachment, setLoadingAttachment] = useState(false);

  // Fetch thumbnail when value changes (uses getById to avoid full-list fetch)
  useEffect(() => {
    if (!value) {
      setAttachment(null);
      return;
    }
    let cancelled = false;
    setLoadingAttachment(true);
    attachmentsService
      .getById(value)
      .then((found) => {
        if (!cancelled) setAttachment(found);
      })
      .catch(() => {
        if (!cancelled) setAttachment(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingAttachment(false);
      });
    return () => { cancelled = true; };
  }, [value]);

  const handleSelect = (id: string) => {
    onChange(id);
    setPickerOpen(false);
  };

  const handleRemoveConfirm = () => {
    onChange(null);
    setRemoveConfirmOpen(false);
  };

  const thumbnailUrl = attachment?.thumbnail ?? attachment?.url ?? null;

  // In vertical mode when an image is selected, the filename at the top of the
  // content area acts as the header — don't duplicate it with a separate label.
  const showLabel = !(layout === 'vertical' && value);
  const isVerticalWithValue = layout === 'vertical' && !!value;

  return (
    <div className={isVerticalWithValue ? 'flex flex-col h-full' : 'space-y-1'}>
      {showLabel && (
        <div className="flex items-center gap-1.5">
          <label htmlFor={fieldId} className="text-sm font-medium text-foreground">{label}</label>
        </div>
      )}

      {hint && showLabel && (
        <p className="text-sm text-muted-foreground">{hint}</p>
      )}

      {/* Content area */}
      {!value ? (
        <div className="flex items-center gap-2">
          <Button
            id={fieldId}
            type="button"
            variant="outline"
            size="sm"
            leftIcon={<ImageIcon className="h-4 w-4" />}
            onClick={() => setPickerOpen(true)}
            disabled={disabled}
          >
            {t('Add image')}
          </Button>
        </div>
      ) : layout === 'vertical' ? (
        <div className="flex flex-col items-end gap-2 h-full">
          {/* Filename */}
          {attachment?.title && (
            <SimpleTooltip content={attachment.title}>
              <p className="text-xs text-muted-foreground truncate max-w-full">
                {attachment.title}
              </p>
            </SimpleTooltip>
          )}

          {/* Large thumbnail */}
          <div className="w-full aspect-square rounded-md overflow-hidden border border-border bg-muted flex items-center justify-center">
            {loadingAttachment ? (
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            ) : thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt={attachment?.title ?? label}
                className="w-full h-full object-cover"
              />
            ) : (
              <ImageIcon className="h-10 w-10 text-muted-foreground" />
            )}
          </div>

          {/* Actions — anchored to the bottom so they align with Save/Cancel
              on the opposite side of a 2-column form. */}
          <div className="mt-auto flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
              onClick={() => setPickerOpen(true)}
              disabled={disabled}
            >
              {t('Change')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              leftIcon={<X className="h-3.5 w-3.5" />}
              onClick={() => setRemoveConfirmOpen(true)}
              disabled={disabled}
              className="!text-destructive hover:!bg-destructive/10"
            >
              {t('Remove')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          {/* Thumbnail */}
          <div className="w-16 h-16 rounded-md overflow-hidden border border-border bg-muted flex items-center justify-center flex-shrink-0">
            {loadingAttachment ? (
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            ) : thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt={attachment?.title ?? label}
                className="w-full h-full object-cover"
              />
            ) : (
              <ImageIcon className="h-6 w-6 text-muted-foreground" />
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-1">
            {attachment?.title && (
              <SimpleTooltip content={attachment.title}>
                <p className="text-xs text-muted-foreground truncate max-w-[140px]">
                  {attachment.title}
                </p>
              </SimpleTooltip>
            )}
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
                onClick={() => setPickerOpen(true)}
                disabled={disabled}
              >
                {t('Change')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                leftIcon={<X className="h-3.5 w-3.5" />}
                onClick={() => setRemoveConfirmOpen(true)}
                disabled={disabled}
                className="!text-destructive hover:!bg-destructive/10"
              >
                {t('Remove')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Attachments picker */}
      {pickerOpen && (
        <AttachmentsManager
          onClose={() => setPickerOpen(false)}
          pickMode={true}
          onSelect={handleSelect}
        />
      )}

      {/* Remove confirmation */}
      <ConfirmDialog
        isOpen={removeConfirmOpen}
        onClose={() => setRemoveConfirmOpen(false)}
        onConfirm={handleRemoveConfirm}
        title={t('Remove image')}
        description={t('Are you sure you want to remove this image?')}
        confirmText={t('Remove')}
        variant="destructive"
      />
    </div>
  );
}
